import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, Subject, defer, throwError } from 'rxjs';
import { catchError, map, mergeMap, tap } from 'rxjs/operators';
import { FileHelper } from '../../../helpers/fileHelper';
import { GuidHelper } from '../../../helpers/guidHelper';
import { IdHelper } from '../../../helpers/idHelper';
import { StringHelper } from '../../../helpers/stringHelper';
import { EPrefix } from '../../../model/EPrefix';
import { IHttpOptions } from '../../../model/IHttpOptions';
import { EApplicationEventType } from '../../../model/application/EApplicationEventType';
import { ConfigData } from '../../../model/config/ConfigData';
import { EStoreFlag } from '../../../model/store/EStoreFlag';
import { ApplicationService } from '../../../services/application.service';
import { ShowMessageParamsPopup } from '../../../services/interfaces/ShowMessageParamsPopup';
import { UiMessageService } from '../../../services/uiMessage.service';
import { OsappError } from '../../errors/model/OsappError';
import { FilesystemService } from '../../filesystem/services/filesystem.service';
import { LogAction } from '../../logger/decorators/log-action.decorator';
import { ELogActionId } from '../../logger/models/ELogActionId';
import { ILogSource } from '../../logger/models/ILogSource';
import { LogActionHandler } from '../../logger/models/log-action-handler';
import { LoggerService } from '../../logger/services/logger.service';
import { OsappApiHelper } from '../../osapp-api/helpers/osapp-api.helper';
import { DmsFile } from '../model/DmsFile';
import { IDms } from '../model/IDms';
import { IDmsData } from '../model/IDmsData';
import { IDmsDocumentAttribute } from '../model/IDmsDocumentAttribute';
import { IDmsEvent } from '../model/IDmsEvent';
import { IDmsMeta } from '../model/IDmsMeta';

interface IServerDmsMeta {
	/** Identifiant du fichier. */
	docId: string,
	/** Date de dernier accès au fichier stringifiée. */
	lastAccessDate: string,
	/** Date de création du fichier stringifiée. */
	createDate: string,
	/** Description du fichier, à l'air d'être constamment "Rapport INCONNU INCONNU". */
	description: string,
	/** Date de modification du fichier stringifiée. */
	modifyDate: string,
	/** MimeType. */
	contentType: string,
	/** Taille du fichier. */
	size: number,
	/** Nom du fichier avec extension. */
	name: string,
	/** Extension du fichier. */
	ext: string,
	/** Type de document. */
	type: string,
	/** Type et extension du fichier : "TYP={type},EXT={ext},". */
	attributes: string
}

/** Service de gestion des fichiers sur le système de fichier en mode remote uniquement. */
@Injectable({ providedIn: "root" })
export class RemoteDmsService implements IDms, ILogSource {

	//#region FIELDS

	/** "/meta" */
	private readonly C_META = "/meta";
	/** Sujet pour l'envoi d'événement. */
	private readonly moDmsEventSubject: Subject<IDmsEvent> = new Subject();

	/** URL qui pointe vers le serveur. */
	private C_SERVER_LINK: string;

	//#endregion

	//#region PROPERTIES

	/** @implements */
	public readonly logSourceId = "RMDS.S::";
	/** @implements */
	public readonly logActionHandler = new LogActionHandler(this);

	//#endregion

	//#region METHODS

	constructor(
		/** Service de gestion des popups et toasts. */
		protected readonly isvcUiMessage: UiMessageService,
		/** Service de gestion des requêtes http. */
		private readonly ioHttpClient: HttpClient,
		/** @implements */
		public readonly isvcLogger: LoggerService,
		private readonly isvcFilesystem: FilesystemService,
		psvcApplication: ApplicationService
	) {
		this.init(psvcApplication);
	}

	/** Initialisation du service qui se sert de données de config.
	 * @param psvcApplication Service de gestion de l'application.
	 */
	private init(psvcApplication: ApplicationService): void {
		psvcApplication.waitForFlag(EStoreFlag.DBInitialized, true)
			.pipe(tap(_ => this.C_SERVER_LINK = `${ConfigData.environment.cloud_url}${ConfigData.environment.cloud_dms_suffix}`))
			.subscribe();
	}

	/** Crée un URL vers le serveur à partir de l'identifiant du fichier à manipuler.
	 * @param psId Identifiant du fichier du DMS avec lequel créer l'URL.
	 */
	private createUrl(psId: string): string {
		return `${this.C_SERVER_LINK}${GuidHelper.extractGuid(psId)}`;
	}

	/** Supprime le fichier sur le serveur ainsi que son méta associé.
	 * @param psId Identifiant du fichier à supprimer.
	 */
	public delete(psId: string): Observable<boolean> {
		const loOptions: IHttpOptions<"arraybuffer"> = this.getHttpBaseOptions("arraybuffer");

		return this.ioHttpClient.delete(this.createUrl(psId), loOptions) // Suppression fichier ET méta sur le serveur.
			.pipe(
				catchError(poError => { console.error(`RDMS.S:: Erreur de suppression du fichier "${psId}" sur le serveur : `, poError); return throwError(poError); }),
				map(_ => {
					console.log(`RMDS.S:: Suppression du fichier "${psId}" sur le serveur réussi !`);
					this.raiseEvent(psId);
					return true;
				})
			);
	}

	/** Lève un événement pour notifier un changement.
	 * @param psFileId Identifiant du fichier.
	 */
	private raiseEvent(psFileId: string, poDmsFile?: DmsFile, poDmsMeta?: IDmsMeta): void {
		const loEvent: IDmsEvent = {
			type: EApplicationEventType.DmsEvent,
			createDate: new Date(),
			data: {
				fileId: psFileId,
				dmsFile: poDmsFile,
				dmsMeta: poDmsMeta
			}
		};

		this.moDmsEventSubject.next(loEvent);
	}

	/** Récupération d'un fichier depuis le serveur à partir de ses méta.
	 * @param psId Identifiant du fichier à récupérer.
	 */
	public get(psId: string): Observable<IDmsData> {
		return this.getMeta(psId)
			.pipe(
				mergeMap((poDmsMetaResult: IDmsMeta) => {
					return this.ioHttpClient.get(this.createUrl(psId), this.getHttpBaseOptions("blob"))
						.pipe(
							tap(
								(poBlob: Blob) => console.debug(`RDMS.S:: Récupération du fichier "${psId}" sur le serveur réussi !`, poBlob),
								poError => console.error(`RDMS.S:: Erreur récupération fichier "${psId}" : `, poError)
							),
							map((poBlobResult: Blob): IDmsData => {
								return {
									file: new DmsFile(this.getBlobWithGoodMimeType(poBlobResult, poDmsMetaResult.name), poDmsMetaResult),
									meta: poDmsMetaResult
								};
							})
						);
				})
			);
	}

	/** Permet de récupérer des options http basiques comprenant les infos utiles pour communiquer avec le serveur.
	 * @param poResponseType Type de la réponse du serveur.
	 */
	private getHttpBaseOptions<T extends "blob" | "arraybuffer" | "json" | "text">(poResponseType: T): IHttpOptions<T> {
		const loHttpBaseOptions: IHttpOptions<T> = {
			responseType: poResponseType,
			headers: { appInfo: OsappApiHelper.stringifyForHeaders(ConfigData.appInfo) }
		};

		if (ConfigData.authentication.token)
			loHttpBaseOptions.headers["token"] = ConfigData.authentication.token;

		return loHttpBaseOptions;
	}

	/** Récupération d'un document de méta du DMS sur le serveur à partir de son identifiant.
	 * @param psId Identifiant du DmsMeta à récupérer.
	 */
	public getMeta(psId: string): Observable<IDmsMeta> {
		//!! Peut créer des problèmes, à surveiller.
		psId = IdHelper.extractIdWithoutPrefix(psId, EPrefix.pendingDownload);

		return this.ioHttpClient.get(`${this.createUrl(psId)}${this.C_META}`, this.getHttpBaseOptions("json"))
			.pipe(
				tap(
					_ => { },
					poError => {
						console.error(`RDMS.S:: Erreur récupération meta "${psId}" : `, poError);
						this.raiseEvent(psId);
					}
				),
				map((poResult: IServerDmsMeta): IDmsMeta => {
					if (psId !== poResult.docId)
						throw new OsappError(`L'identifiant retourné par le serveur '${poResult.docId}' ne correspond pas à l'identifiant demandé '${psId}'.`);

					return {
						_id: IdHelper.buildId(EPrefix.dms, poResult.docId),
						description: poResult.description,
						size: poResult.size,
						name: poResult.name,
						documentType: poResult.type,
						criteria: []
					};
				})
			);
	}

	/** Récupère un blob avec le bon type mime (clone celui en paramètre s'il n'a pas un type mime correct).
	 * @param poBlob Blob qu'il faut peut-être cloner pour obtenir un clone avec le bon type mime.
	 * @param psName Nom du fichier que représente le blob (avec extension).
	 */
	private getBlobWithGoodMimeType(poBlob: Blob, psName: string): Blob {
		// Cas `image/*` notamment : sur webapp, si on tente de télécharger des images prises par un mobile on peut ne pas avoir d'extension automatiquement
		// lors de l'enregistrement du fichier sur le pc.

		if (poBlob.type.includes("*")) {
			const lsMimeType: string | undefined = FileHelper.extractMimeTypeFromFileNameWithExtension(psName);
			return lsMimeType ? FileHelper.cloneBlob(poBlob, lsMimeType) : poBlob;
		}
		else
			return poBlob;
	}

	/** Enregistre un fichier du dossier DMS sur le serveur (téléversement).
	 * @param poData Objet contenant le fichier sous forme de Blob ou base64 ainsi que son nom, sa taille, son mimeType.
	 * @param poMeta Méta du document.
	 */
	@LogAction<Parameters<RemoteDmsService["save"]>, ReturnType<RemoteDmsService["save"]>>({
		actionId: ELogActionId.dmsDocUpload,
		successMessage: "Document (GED) transmis au serveur.",
		errorMessage: "Erreur lors de la transmission du document (GED) au serveur.",
		dataBuilder: (_, poMeta: IDmsMeta, __, poMetaParam?: IDmsMeta) => ({ metaId: poMeta?._id ?? poMetaParam._id })
	})
	public save(poData: DmsFile, poMeta: IDmsMeta): Observable<IDmsMeta> {
		if (!poData) {
			return throwError(new HttpErrorResponse({
				error: "Le document à téléverser n'existe pas localement.",
				status: 400
			}));
		}
		else if (StringHelper.isBlank(poData.Path)) { // Cas Blob ou base64 qu'on transforme en blob.
			return this.upload(
				poData.File instanceof Blob ? poData.File : FileHelper.base64toBlob({ base64: poData.File }, poData.MimeType),
				poMeta
			);
		}
		else // Chemin d'accès.
			return defer(() => this.isvcFilesystem.getFileAsync(poData.Path)).pipe(mergeMap((poBlobResult: Blob) => this.upload(poBlobResult, poMeta)));
	}

	/** Permet de s'abonner aux événements du RemoteDmsService.
	 * @param pfNext Fonction appelée lors du next => function(poResult: IDmsEvent).
	 * @param pfError Fonction appelée lors du error => function(poError: any).
	 */
	public subscribe(pfNext: Function, pfError?: Function): void {
		this.moDmsEventSubject.asObservable()
			.subscribe(
				(poResult: IDmsEvent) => pfNext(poResult),
				poError => pfError(poError)
			);
	}

	/** Téléverse le fichier vers le serveur qui créera aussi la méta associée.
	 * @param poFile Fichier qu'il faut téléverser, sous forme de `Blob` ou `File`.
	 * @param poDmsMeta Objet méta associé au fichier qu'on téléverse.
	 */
	private upload(poFile: Blob | File, poDmsMeta: IDmsMeta): Observable<IDmsMeta> {
		const loFormData = new FormData();
		const loOptions: IHttpOptions<"text"> = this.getHttpBaseOptions("text");

		loFormData.append("file", poFile instanceof File ? FileHelper.cloneFile(poFile) : FileHelper.cloneBlob(poFile), poDmsMeta.name);

		loOptions.params = { DocumentMeta: JSON.stringify(this.convertToRemoteDmsMeta(poDmsMeta)) };

		return this.ioHttpClient.post(this.createUrl(poDmsMeta._id), loFormData, loOptions)
			.pipe(
				tap(
					(psGuid: string) => console.log(`RDMS.S:: Upload du fichier "${poDmsMeta.name}" sur le serveur réussi : ${psGuid}`),
					(poError: HttpErrorResponse) => {
						console.error(`RDMS.S:: Erreur post fichier "${poDmsMeta.name}" sur le serveur :`, poError)
						this.isvcUiMessage.showMessage(
							new ShowMessageParamsPopup({
								header: "Erreur",
								message: JSON.parse(poError.error).message
							})
						);
					}
				),
				map(_ => {
					this.raiseEvent(poDmsMeta._id, new DmsFile(poFile, poDmsMeta), poDmsMeta);
					return poDmsMeta;
				})
			);
	}

	private convertToRemoteDmsMeta(poDmsMeta: IDmsMeta): { type: string, attributes: string } {
		return { type: poDmsMeta.documentType, attributes: this.convertAttributesToCsvString(poDmsMeta) };
	}

	/** Transforme le tableau d'attributs des metas en chaîne CSV.
	 * @param poDmsMeta
	 * @returns Peut retourner `undefined` si le tableau n'est pas défini.
	 */
	private convertAttributesToCsvString(poDmsMeta: IDmsMeta): string {
		return poDmsMeta.attributes?.map((poAttribute: IDmsDocumentAttribute) => `${poAttribute.name}=${poAttribute.value},`).join(""); //On force aucun séparateur pour laisser une virgule en fin, comme attendu par le webservice.
	}

	//#endregion

}
